import { ScoreUtils } from '@assets/model/ScoreUtils';
import { useStore } from '@assets/model/Store';
import { observer } from 'mobx-react';
import React, { PureComponent, useRef, useState } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LabelList,
    ComposedChart,
    Line,
    Cell
} from 'recharts';

import * as style from './ChartStyles.module.less';
import { Flex } from '@assets/component/UI/Flex/Flex';
import { formatPercent } from '../Financial/PanelCompanyYearly/PanelElements/Formatters';
import { CompanyCompareUtils } from '@assets/model/CompanyCompareUtils';
import { observable } from 'mobx';
import { ChartUtils } from './ChartShared';
import { FieldsSchema } from '@assets/model/FieldsSchema';

interface IChartCompanyRevenue {
    reportId: string;
}

export class TickInfo {
    @observable accessor tickSize: number = 0;
}

export const ChartCompanyRevenue = observer((p: IChartCompanyRevenue) => {
    const tickInfo = useRef({ firstTick: 0, secondTick: 0 });
    const [tickStore] = useState(new TickInfo());

    const store = useStore();
    const q = store.createDataAccess(p.reportId);
    const prefEbitdaType = q?.selectedEbitdaType;

    var schemaLookupName = 'Adjusted EBITDA';
    FieldsSchema[prefEbitdaType]['display'] ?? 'Adjusted EBITDA';

    const NAME_LOOKUP: { [fieldName: string]: string } = {
        adj_ebitda_margin: `${schemaLookupName} Margin`,
        EBITDA: schemaLookupName,
        Revenue: 'Revenue'
    };

    if (q == null) return null;

    var data = [];

    var { from, to } = q.getReportYearRange();
    const maxYears = 11;
    if (to - from > maxYears) {
        from = to - maxYears;
    }

    const ebitdaType = q.selectedEbitdaType;

    for (var year = from; year <= to; year++) {
        var yearEntry = {};
        const isActualized = q.getIsYearActualized(year);
        const letter = isActualized ? 'A' : 'P';
        yearEntry['year'] = year + letter;

        yearEntry['Revenue'] = q.valueByYear('net_revenue_per_year', year);
        if (yearEntry['Revenue'] == 'N/A') {
            yearEntry['Revenue'] = 0;
        } else {
            yearEntry['Revenue'] = parseFloat(yearEntry['Revenue']);
        }

        yearEntry['EBITDA'] = q.valueByYear(prefEbitdaType, year);
        if (yearEntry['EBITDA'] == 'N/A') {
            var ebitda = q.valueByYear('ebitda_per_year', year);
            if (ebitda != 'N/A') {
                yearEntry['EBITDA'] = ebitda;
            } else {
                yearEntry['EBITDA'] = 0;
            }
        } else {
            yearEntry['EBITDA'] = parseFloat(yearEntry['EBITDA']);
        }

        yearEntry['adj_ebitda_margin'] = q.selectedEbitdaMargin(year);

        if (yearEntry['adj_ebitda_margin'] == 'N/A') {
            var ebitdaMargin = q.ebitdaMargin(year);
            if (ebitdaMargin != 'N/A') {
                yearEntry['adj_ebitda_margin'] = ebitdaMargin;
            }
        }

        if (yearEntry['adj_ebitda_margin'] != 'N/A') {
            yearEntry['adj_ebitda_margin'] *= 100;
        }

        data.push(yearEntry);
    }

    const CustomYearLabelsTick = params => {
        if (params.index === 0) {
            tickInfo.current.firstTick = params.x;
        } else if (params.index === 1) {
            tickInfo.current.secondTick = params.x;

            const diff =
                tickInfo.current.secondTick - tickInfo.current.firstTick;
            tickStore.tickSize = diff;
        }

        const { x, y, payload } = params;

        const bandWidth = tickStore.tickSize;

        return (
            <text
                x={x + bandWidth / 2}
                y={y + 14}
                fontSize="14"
                textAnchor="middle"
            >
                {payload.value}
            </text>
        );
    };

    return (
        <Flex direction="column" fillCell={true}>
            <div className={style.title}>Financial Overview</div>
            <ResponsiveContainer
                width="100%"
                height="100%"
                onResize={() => {
                    tickInfo.current = { firstTick: 0, secondTick: 0 };
                    tickStore.tickSize = 0;
                }}
            >
                <ComposedChart
                    data={data}
                    margin={{
                        top: 20,
                        left: 20,
                        right: 20,
                        bottom: 20
                    }}
                >
                    <defs>{ChartUtils.getBarGradient()}</defs>
                    <CartesianGrid stroke="#f5f5f5" />

                    <YAxis
                        domain={[0, 'auto']}
                        yAxisId="left"
                        tickFormatter={value => `$${value.toFixed()}M`}
                        interval="preserveStartEnd"
                    />
                    <YAxis yAxisId="right" hide={true} />
                    <Legend
                        verticalAlign="bottom"
                        formatter={v => {
                            return NAME_LOOKUP[v];
                        }}
                        wrapperStyle={{
                            fontFamily: 'LustTextMedium',
                            fontSize: 12
                        }}
                    />
                    <Bar
                        dataKey="Revenue"
                        fill="url(#barGradientBlue)"
                        yAxisId="left"
                        radius={[10, 10, 0, 0]}
                    >
                        <LabelList
                            dataKey="Revenue"
                            position="top"
                            formatter={ChartUtils.formatCurrency}
                            offset={5}
                            fill="black"
                        />

                        {data.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                style={{
                                    filter: ChartUtils.SHADOW_FILTER
                                }}
                            />
                        ))}
                    </Bar>
                    <Bar
                        dataKey="EBITDA"
                        fill="url(#barGradientGold)"
                        yAxisId="left"
                        radius={[10, 10, 0, 0]}
                    >
                        <LabelList
                            dataKey="EBITDA"
                            position="top"
                            formatter={ChartUtils.formatCurrency}
                            offset={10}
                            fill="black"
                        />

                        {data.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                style={{
                                    filter: ChartUtils.SHADOW_FILTER
                                }}
                            />
                        ))}
                    </Bar>
                    <Line
                        type="monotone"
                        dataKey="adj_ebitda_margin"
                        stroke="#d9c666"
                        yAxisId="right"
                    >
                        <LabelList
                            dataKey="adj_ebitda_margin"
                            position="top"
                            formatter={ChartUtils.formatPercent}
                            fill="black"
                            offset={10}
                        />
                    </Line>
                    <Tooltip
                        formatter={(v, name, payload) => {
                            switch (name) {
                                case 'adj_ebitda_margin':
                                    return [
                                        ChartUtils.formatPercent(v as number),
                                        NAME_LOOKUP[name]
                                    ];
                                case 'EBITDA':
                                    return [
                                        ChartUtils.formatCurrency(v as number),
                                        NAME_LOOKUP[name]
                                    ];
                                default:
                                    return [
                                        ChartUtils.formatCurrency(v as number),
                                        NAME_LOOKUP[name]
                                    ];
                            }
                        }}
                    />
                    <XAxis
                        dataKey="year"
                        scale="band"
                        interval="preserveStartEnd"
                        tick={CustomYearLabelsTick}
                    />
                </ComposedChart>
            </ResponsiveContainer>
        </Flex>
    );
});
